import React from "react";
import { Route, Navigate } from "react-router-dom";
import MainLayout from "../../components/MainLayout/MainLayout";
import { AuthenticationGuard } from "@/components/AuthenticationGuard/AuthenticationGuard";
import PermissionGuard from "../../components/PermissionGuard/PermissionGuard";
import HubDashboard from "../../modules/hub/features/HubDashboard/HubDashboard";
import MonthlyBalanceSheet from "../../modules/hub/features/FinancialReports/components/MonthlyBalanceSheet/MonthlyBalanceSheet";
import ProfitAndLoss from "../../modules/hub/features/FinancialReports/components/ProfitAndLoss/ProfitAndLoss";
import ProfitAndLossBuilder from "../../modules/hub/features/FinancialReports/components/ProfitAndLossBuilder/ProfitAndLossBuilder";
import TransactionList from "../../modules/hub/features/FinancialReports/components/TransactionList/TransactionList";
import DataConnector from "../../modules/hub/features/HotglueDataConnector/components/DataConnector/DataConnector";
import UserList from "../../modules/hub/features/UserManagement/components/UserList/UserList";
import EditUser from "../../modules/hub/features/UserManagement/components/EditUser/EditUser";
import CreateUser from "../../modules/hub/features/UserManagement/components/CreateUser/CreateUser";
import SettingsLayout from "../../modules/hub/features/UserAppSettings/components/SettingsLayout/SettingsLayout";
import UserProfile from "../../modules/hub/features/UserAppSettings/components/UserProfile/UserProfile";
import CompanyProfile from "../../modules/hub/features/UserAppSettings/components/CompanyProfile/CompanyProfile";
import ServiceTitanSettings from "../../modules/hub/features/UserAppSettings/components/ServiceTitanSettings/ServiceTitanSettings";
import QuickBooksSettings from "../../modules/hub/features/UserAppSettings/components/QuickBooksSettings/QuickBooksSettings";
import AnnualRevenue from "../../modules/hub/features/UserAppSettings/components/AnnualRevenue/AnnualRevenue";
import Integrations from "../../modules/hub/features/UserAppSettings/components/Integrations/Integrations";
import CoachingDashboard from "@/modules/hub/features/CoachingDashboard/components/CoachingDasboard/CoachingDashboard";
import { ResourcesLibrary as ResourceLibrary } from "@/modules/hub/features/ResourceLibrary/components/ResourceLibrary/ResourceLibrary";
import ResourceDetails from "@/modules/hub/features/ResourceLibrary/components/ResourceDetails/ResourceDetails";
import { Podcasts } from "@/modules/hub/features/Podcasts/components/Podcasts/Podcasts";
import { News } from "@/modules/hub/features/News/components/News/News";
import LeadGeneration from "@/modules/hub/features/LeadGeneration/components/LeadGeneration/LeadGeneration";

// Create stable component reference
const HubMainLayout = () => <MainLayout section="hub" />;

/**
 * HubRoutes
 *
 * Wraps the /hub section in the Auth0-based authentication guard, then
 * protects individual routes with PermissionGuard based on the required
 * permissions from your navigation config.
 */
const HubRoutes = (
  <Route
    element={<AuthenticationGuard component={HubMainLayout} />}
    path="/hub"
  >
    <Route element={<HubDashboard />} index />

    <Route element={<CoachingDashboard />} path="dashboards/coaching" />

    {/* Financial Reports routes */}
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["financial-reports-monthly-balance"]}
        >
          <MonthlyBalanceSheet />
        </PermissionGuard>
      }
      path="financial-reports/monthly-balance-sheet"
    />
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["financial-reports-profit-and-loss"]}
        >
          <ProfitAndLoss />
        </PermissionGuard>
      }
      path="financial-reports/profit-and-loss"
    />
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["financial-reports-p-and-l-report-builder"]}
        >
          <ProfitAndLossBuilder />
        </PermissionGuard>
      }
      path="financial-reports/profit-and-loss-builder"
    />
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["financial-reports-transaction-list"]}
        >
          <TransactionList />
        </PermissionGuard>
      }
      path="financial-reports/transaction-list"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["data-connector"]}>
          <DataConnector />
        </PermissionGuard>
      }
      path="data-connector"
    />

    {/* User Management routes - accessible via user-management permission */}
    <Route
      element={
        <PermissionGuard requiredPermissions={["user-management"]}>
          <UserList />
        </PermissionGuard>
      }
      path="users"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["user-management"]}>
          <CreateUser />
        </PermissionGuard>
      }
      path="users/create"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["user-management"]}>
          <EditUser />
        </PermissionGuard>
      }
      path="users/:uuid/edit"
    />

    {/* Settings - nested routes with side navigation */}
    <Route element={<SettingsLayout />} path="settings">
      <Route element={<Navigate replace to="profile" />} index />
      <Route element={<UserProfile />} path="profile" />
      <Route
        element={
          <PermissionGuard requiredPermissions={["company-profile"]}>
            <CompanyProfile />
          </PermissionGuard>
        }
        path="company"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["integrations"]}>
            <Integrations />
          </PermissionGuard>
        }
        path="integrations"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["integrations"]}>
            <ServiceTitanSettings />
          </PermissionGuard>
        }
        path="integrations/servicetitan"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["integrations"]}>
            <QuickBooksSettings />
          </PermissionGuard>
        }
        path="integrations/quickbooks"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["annual-revenue"]}>
            <AnnualRevenue />
          </PermissionGuard>
        }
        path="annual-revenue"
      />
    </Route>

    <Route element={<ResourceLibrary />} path="resources" />
    <Route element={<ResourceDetails />} path="resources/:slug" />

    <Route element={<News />} path="news" />
    <Route element={<Podcasts />} path="podcasts" />

    <Route element={<LeadGeneration />} path="lead-generation" />
  </Route>
);

export default HubRoutes;
